/**
 * Step3AssignAttesters.js
 *
 * WHAT: Step 3 of the campaign workflow -- assigns an attester to every in-scope database account.
 * WHY:  Each database account must have exactly one person responsible for reviewing it.
 *       This step auto-applies attester rules (enterprise manager, account owner overrides,
 *       database owner) and surfaces any unmatched accounts that need manual assignment,
 *       ensuring full coverage before the campaign can be activated.
 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import API_CONFIG from '../../config/api.config';

/**
 * WHAT: Unified attester assignment panel that auto-applies rules and highlights unmatched accounts.
 * WHY:  Automates the tedious process of mapping every database account to a reviewer while
 *       ensuring no account slips through without an assigned attester -- a key audit requirement.
 */
const UnifiedAttesterAssignment = ({ campaign, activeStep, step2ModalOpen }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [unmatchedUsers, setUnmatchedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [enterpriseUsersWithoutManagers, setEnterpriseUsersWithoutManagers] = useState([]);
  const [showManagerWarningModal, setShowManagerWarningModal] = useState(false);

  useEffect(() => {
    if (campaign && campaign.campaign_id && activeStep === 3) {
      // Automatically apply attester rules when navigating to Step 3
      applyAllRulesOnLoad();
      // Only load manager warnings after the Step 2 success modal has been dismissed.
      // When Step 2 completes, auto-advance mounts Step 3 while the Step 2 modal is
      // still open — showing the manager warning on top of it creates a confusing UX.
      if (!step2ModalOpen) {
        loadEnterpriseUsersWithoutManagers();
      }
    }
  }, [campaign, activeStep, step2ModalOpen]);

  // Automatically apply all attester rules when Step 3 loads
  const applyAllRulesOnLoad = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint('attestation/apply-all-attester-rules/'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ campaign_id: campaign.campaign_id })
        }
      );

      const data = await response.json();

      if (data.success) {
        setResults({
          ...data.results,
          can_proceed: data.can_proceed
        });
        // Load unmatched users after applying rules
        await loadUnmatchedUsers();
      } else {
        setError(data.error || 'Failed to apply attester rules');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Load enterprise users without managers for this campaign
  const loadEnterpriseUsersWithoutManagers = async () => {
    try {
      const params = new URLSearchParams();
      params.append('campaign_id', campaign.campaign_id);
      params.append('_t', Date.now());

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attestation/enterprise-users-without-managers/?${params.toString()}`), {
        headers: {
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const data = await response.json();

      if (data.success && data.users_without_managers && data.users_without_managers.length > 0) {
        setEnterpriseUsersWithoutManagers(data.users_without_managers);
        setShowManagerWarningModal(true);
      } else {
        setEnterpriseUsersWithoutManagers([]);
      }
    } catch (err) {
      console.error('Error loading enterprise users without managers:', err);
    }
  };

  const loadUnmatchedUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (campaign.campaign_id) {
        params.append('campaign_id', campaign.campaign_id);
      }
      params.append('filter_in_scope', 'true');
      params.append('_t', Date.now());

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attestation/prepare/?${params.toString()}`), {
        headers: {
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const data = await response.json();

      if (data.success) {
        setUnmatchedUsers(data.unmatched_users || []);
      } else {
        setError('Failed to load attestation data');
      }
    } catch (err) {
      setError('Error loading attestation data: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || isProcessing) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">
          {isProcessing ? 'Assigning attesters to in-scope users...' : 'Loading attestation data...'}
        </p>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <style>
        {`
          .unified-attester-assignment {
            padding: 1rem;
          }
          .priority-flow {
            display: flex;
            align-items: stretch;
            justify-content: center;
            margin: 2rem 0;
            flex-wrap: wrap;
            gap: 1rem;
          }
          .priority-card {
            flex: 0 0 220px;
            width: 220px;
            min-height: 140px;
            padding: var(--space-lg);
            border: 2px solid var(--border-light);
            border-radius: var(--radius-lg);
            text-align: center;
            background: var(--background-secondary);
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
          }
          .priority-card:hover {
            border-color: var(--primary-500);
            box-shadow: var(--shadow-md);
          }
          .priority-number {
            position: absolute;
            top: -12px;
            left: 12px;
            background: var(--primary-500);
            color: white;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 0.9rem;
          }
          .priority-card h4 {
            margin: 0.5rem 0;
            font-size: 1rem;
            color: var(--text-primary);
          }
          .priority-card p {
            margin: 0.5rem 0;
            font-size: 0.85rem;
            color: var(--text-secondary);
          }
          .priority-card .count {
            display: inline-block;
            margin-top: 0.5rem;
            padding: 0.25rem 0.75rem;
            background: var(--primary-500);
            color: white;
            border-radius: 12px;
            font-size: 0.85rem;
            font-weight: bold;
          }
          .results-section {
            margin-top: 2rem;
            padding: var(--space-lg);
            background: var(--info-light);
            border-radius: var(--radius-lg);
            border: 1px solid var(--info);
          }
          .results-section h4 {
            margin-bottom: 1rem;
            color: var(--primary-700);
          }
          .results-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
          }
          .result-card {
            padding: 1rem;
            background: white;
            border-radius: var(--radius-md);
            border: 1px solid var(--border-light);
            display: flex;
            align-items: center;
            gap: 1rem;
          }
          .result-card i {
            font-size: 2rem;
            color: var(--success);
          }
          .result-card.warning i {
            color: var(--warning);
          }
          .result-card.danger i {
            color: var(--danger);
          }
          .result-content {
            flex: 1;
          }
          .result-label {
            display: block;
            font-size: 0.85rem;
            color: var(--text-secondary);
            margin-bottom: 0.25rem;
          }
          .result-value {
            display: block;
            font-size: 1.5rem;
            font-weight: bold;
            color: var(--text-primary);
          }
          .result-percentage {
            font-size: 0.9rem;
            color: var(--text-secondary);
            margin-left: 0.5rem;
          }
        `}
      </style>

      <div className="unified-attester-assignment">
            <div className="alert alert-info mb-4">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Attester Assignment Priority:</strong> This step applies all attester assignment rules in priority order:
              (1) User Manager from Azure AD, (2) Account Owners, (3) Database Owners, (4) Application Owners.
            </div>

            {/* Priority Flow Visualization */}
            <div className="priority-flow">
              <div className="priority-card">
                <span className="priority-number">1</span>
                <h4>User Manager</h4>
                <p>Direct manager from Azure AD</p>
                {results && <span className="count">{results.enterprise_manager_count} users</span>}
              </div>

              <div className="priority-card">
                <span className="priority-number">2</span>
                <h4>Account Owners</h4>
                <p>Manually assigned account owners</p>
                {results && <span className="count">{results.override_count} users</span>}
              </div>

              <div className="priority-card">
                <span className="priority-number">3</span>
                <h4>Database Owners</h4>
                <p>DBA/database administrators</p>
                {results && <span className="count">{results.database_owner_count} users</span>}
              </div>

              <div className="priority-card">
                <span className="priority-number">4</span>
                <h4>Application Owners</h4>
                <p>App owners via database mapping</p>
                {results && <span className="count">{results.application_owner_count} users</span>}
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="alert alert-danger mt-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* Results Display */}
            {results && (
              <div className="results-section">
                <h4>
                  <i className="fas fa-chart-bar me-2"></i>
                  Assignment Results
                </h4>
                <div className="results-grid">
                  <div className="result-card">
                    <i className="fas fa-users"></i>
                    <div className="result-content">
                      <span className="result-label">Total In Scope</span>
                      <span className="result-value">{results.total_in_scope}</span>
                    </div>
                  </div>

                  <div className="result-card">
                    <i className="fas fa-check-circle"></i>
                    <div className="result-content">
                      <span className="result-label">Matched Users</span>
                      <span className="result-value">
                        {results.total_in_scope - results.unmatched_count}
                      </span>
                      <span className="result-percentage">
                        ({results.matched_percentage}%)
                      </span>
                    </div>
                  </div>

                  <div className={`result-card ${results.unmatched_count > 0 ? 'warning' : ''}`}>
                    <i className={`fas ${results.unmatched_count > 0 ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
                    <div className="result-content">
                      <span className="result-label">Unmatched Users</span>
                      <span className="result-value">{results.unmatched_count}</span>
                    </div>
                  </div>
                </div>

                {results.can_proceed ? (
                  <div className="alert alert-success mt-3">
                    <i className="fas fa-check-circle me-2"></i>
                    <strong>All users assigned!</strong> You can proceed to the next step.
                  </div>
                ) : (
                  <div className="alert alert-warning mt-3">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Warning:</strong> {results.unmatched_count} user(s) still need attester assignment.
                    You may need to add account owner assignments or configure additional database/application owners.
                  </div>
                )}
              </div>
            )}

            {/* Unmatched Users List */}
            {unmatchedUsers.length > 0 && (
              <div className="mt-4">
                <h6 className="text-danger">
                  <i className="fas fa-user-times me-2"></i>
                  Unmatched Users ({unmatchedUsers.length})
                </h6>
                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table table-sm table-hover">
                    <thead className="table-dark sticky-top">
                      <tr>
                        <th>Database User</th>
                        <th>Database</th>
                        <th>Employee</th>
                        <th>Department</th>
                        <th>Privilege Level</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmatchedUsers.map((user, idx) => (
                        <tr key={idx}>
                          <td>{user.db_username}</td>
                          <td>{user.database_name}</td>
                          <td>{user.user_display_name || 'N/A'}</td>
                          <td>{user.user_department || 'N/A'}</td>
                          <td>
                            <span className={`badge bg-${
                              user.privilege_level === 'CRITICAL' ? 'danger' :
                              user.privilege_level === 'HIGH' ? 'warning' :
                              user.privilege_level === 'MEDIUM' ? 'info' : 'secondary'
                            }`}>
                              {user.privilege_level || 'UNKNOWN'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
      </div>

      {/* Enterprise Users Without Managers Warning Banner */}
      {enterpriseUsersWithoutManagers.length > 0 && (
        <div
          className="alert alert-warning border-warning mb-3 mx-3"
          style={{ cursor: 'pointer' }}
          onClick={() => setShowManagerWarningModal(true)}
        >
          <div className="d-flex align-items-center">
            <i className="fas fa-exclamation-triangle fs-4 me-3"></i>
            <div className="flex-grow-1">
              <strong className="d-block mb-1">
                {enterpriseUsersWithoutManagers.length} Enterprise User{enterpriseUsersWithoutManagers.length !== 1 ? 's' : ''} Without Manager in Azure AD
              </strong>
              <small>
                These in-scope users match enterprise users but their Azure AD accounts don't have managers assigned.
                They will be assigned to a fallback attester (Account Owner, Database Owner, or Application Owner).
              </small>
            </div>
            <button
              className="btn btn-sm btn-warning ms-2"
              onClick={(e) => {
                e.stopPropagation();
                setShowManagerWarningModal(true);
              }}
            >
              <i className="fas fa-eye me-1"></i>
              View Details
            </button>
          </div>
        </div>
      )}

      {/* Manager Warning Modal - Using Portal to render at document root */}
      {showManagerWarningModal && ReactDOM.createPortal(
        <div tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ margin: 0, maxWidth: '900px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '2px solid #333' }}>
              <div style={{ flexShrink: 0, backgroundColor: '#ffc107', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '16px', borderBottom: '2px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h5 style={{ color: '#000', fontWeight: '600', margin: 0, fontSize: '1.25rem' }}>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Enterprise Users Without Managers in Azure AD
                </h5>
                <button
                  type="button"
                  onClick={() => setShowManagerWarningModal(false)}
                  aria-label="Close"
                  style={{ background: 'transparent', border: 'none', fontSize: '24px', fontWeight: '700', lineHeight: 1, color: '#000', cursor: 'pointer', padding: '0', opacity: 0.5 }}
                  onMouseOver={(e) => e.target.style.opacity = '1'}
                  onMouseOut={(e) => e.target.style.opacity = '0.5'}
                >×</button>
              </div>
              <div style={{ overflowY: 'auto', flex: '1 1 auto', backgroundColor: '#ffffff', padding: '20px' }}>
                <div className="alert alert-warning mb-3" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '12px 16px', color: '#856404' }}>
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-users-slash fs-4 me-3" style={{ color: '#856404' }}></i>
                    <div>
                      <strong className="fs-5" style={{ fontWeight: '700', color: '#212529' }}>{enterpriseUsersWithoutManagers.length}</strong>
                      <span className="ms-2" style={{ color: '#212529' }}>in-scope enterprise user{enterpriseUsersWithoutManagers.length !== 1 ? 's' : ''} do not have managers assigned in Azure AD</span>
                    </div>
                  </div>
                </div>

                <p className="mb-3" style={{ color: '#212529' }}>
                  These database users are matched to enterprise users (via Employee ID), but their Azure AD accounts don't have a manager assigned.
                  In such cases, the system will assign them to the next available attester in this order: Account Owner, then Database Owner, then Application Owner.
                </p>

                <div className="alert alert-info mb-3" style={{ backgroundColor: '#d1ecf1', border: '1px solid #0dcaf0', borderRadius: '4px', padding: '12px 16px', color: '#055160' }}>
                  <i className="fas fa-info-circle me-2" style={{ color: '#055160' }}></i>
                  <strong>Recommendation:</strong> Alternatively, you can update Azure AD to assign managers to these users, or manually assign an Account Owner for them in the "Account Owners" configuration.
                </div>

                <div className="card border-warning mb-3" style={{ border: '1px solid #ffc107' }}>
                  <div className="card-header bg-warning text-dark" style={{ backgroundColor: '#ffc107', color: '#212529', padding: '12px 16px', fontWeight: '600', textAlign: 'left' }}>
                    Affected Users ({enterpriseUsersWithoutManagers.length})
                  </div>
                  <div className="card-body" style={{ maxHeight: '350px', overflowY: 'auto', backgroundColor: '#fff', padding: '0' }}>
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-dark sticky-top">
                        <tr>
                          <th>DB Username</th>
                          <th>Database</th>
                          <th>Employee ID</th>
                          <th>Enterprise Name</th>
                          <th>Department</th>
                          <th>Privilege Level</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enterpriseUsersWithoutManagers.map((user, idx) => (
                          <tr key={idx}>
                            <td><strong>{user.db_username}</strong></td>
                            <td>{user.database_name}</td>
                            <td><code>{user.employee_id}</code></td>
                            <td>{user.enterprise_display_name || 'N/A'}</td>
                            <td>{user.enterprise_department || 'N/A'}</td>
                            <td>
                              <span className={`badge bg-${
                                user.privilege_level === 'SUPER_ADMIN' || user.privilege_level === 'ADMIN' ? 'danger' :
                                user.privilege_level === 'WRITE_ELEVATED' ? 'warning' :
                                user.privilege_level === 'READ_ELEVATED' ? 'info' : 'secondary'
                              }`}>
                                {user.privilege_level || 'UNKNOWN'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div style={{ flexShrink: 0, backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6', padding: '16px', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowManagerWarningModal(false)}
                  style={{ backgroundColor: '#2196f3', border: '1px solid #2196f3', borderRadius: '4px', color: '#fff', padding: '10px 20px', fontWeight: '500', cursor: 'pointer', fontSize: '1rem' }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#1976d2'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#2196f3'}
                >
                  <i className="fas fa-check me-2"></i>
                  I Understand
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UnifiedAttesterAssignment;
